import { v4 as uuidv4 } from 'uuid';

type Maybe<T> = T | null | undefined;

export interface ErrorReportDto {
  IS_ERROR_REPORT: true;
  ERROR_REPORT_VERSION: string;
  uuid: string;
  ordinal: number | null;
  message: Maybe<string>;
  constructorName: Maybe<string>;
  prototypeTree: string[];
  typeof: string;
  stack: Maybe<string>;
  timestamp: Date;
  error: any;
}

export class ErrorReport {
  public readonly IS_ERROR_REPORT = true;
  public readonly ERROR_REPORT_VERSION = '1.0';

  /**
   * A UUID to uniquely identify this report.
   */
  public uuid = uuidv4();

  /**
   * An ordinal to describe which error report this was in a series.
   */
  public ordinal: number | null = null;

  /**
   * The internal error message, if available.
   */
  public message: Maybe<string>;
  /**
   * The name of the error constructor, if any.
   */
  public constructorName: Maybe<string>;
  /**
   * The prototype hierarchy of the error object.
   */
  public prototypeTree: string[];
  /**
   * The result of `typeof` on the error object.
   */
  public typeof: string;
  /**
   * The error stack, if any.
   */
  public stack: Maybe<string>;
  /**
   * The time this error occurred.
   */
  public timestamp: Date;

  constructor(
    /**
     * The error object. This is anything that's been thrown. It will usually be an `Error`, but might not be.
     */
    public error: any
  ) {
    this.message = (error as Error)?.message;
    this.constructorName = error?.constructor?.name;
    this.prototypeTree = this.createPrototypeTree(error);
    this.typeof = typeof error;
    this.stack = (error as Error)?.stack;
    this.timestamp = new Date();
  }

  protected createPrototypeTree(error: any): string[] {
    const prototypes = this.getAllAncestors(error);
    return prototypes.map(p => p.constructor?.name);
  }

  protected getAllAncestors(current: any, list: any[] = []): any[] {
    const proto = current?.__proto__;

    const limit = 99;
    if (!current || !proto || list.includes(proto) || list.length >= limit) {
      return list;
    }

    list.push(proto);
    this.getAllAncestors(proto, list);

    return list;
  }
}
